import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClipboardDocumentIcon,
  CreditCardIcon,
  EyeIcon,
  EyeSlashIcon,
  HomeIcon,
  InformationCircleIcon,
  LinkSlashIcon,
  MinusIcon,
  PencilSquareIcon,
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
  PlusCircleIcon,
  PlusIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getBearerHeaders } from '../lib/auth';
import { cn } from '../lib/utils';

const PLAN_WHATSAPP_FREE = 'whatsapp_desktop_free';
const PLAN_WHATSAPP_ENTERPRISE = 'whatsapp_desktop_enterprise';

const DELETE_CONFIRM_MSG =
  'Tem certeza que deseja deletar a instância?\n\nEsta ação não pode ser desfeita.';

const CREATE_FLOW_PLAN = 'plan';
const CREATE_FLOW_ENTERPRISE_CHECKOUT = 'enterprise_checkout';
const CREATE_FLOW_ENTERPRISE_PAYMENT = 'enterprise_payment';
const CREATE_FLOW_ENTERPRISE_FINALIZE = 'enterprise_finalize';

const BILLING_RECURRENT = 'RECURRENT';
// const BILLING_UNIQUE = 'UNIQUE'; // fluxo Único desativado no painel — apenas RECORRENTE (mensal)

/** Preço por instância exibido no painel (cobrança Discador.NET via Asaas). */
const ENTERPRISE_INSTANCE_PRICE_BRL = 100;

const WAVOIP_SIP_HOST_DEFAULT =
  (typeof process !== 'undefined' && process.env.REACT_APP_WAVOIP_SIP_HOST) || '141.11.73.93';

const SIP_TRANSPORT_OPTIONS = ['UDP', 'TCP', 'TLS'];

const DISCADOR_PAYMENT_PENDING_LEGEND =
  'Seu pagamento está pendente. Assim que o pagamento for confirmado, você poderá acessar todos os recursos do seu dispositivo.';

function enterpriseTotalBrl(quantity) {
  const q = Math.max(1, Math.min(99, parseInt(quantity, 10) || 1));
  return ENTERPRISE_INSTANCE_PRICE_BRL * q;
}

function enterpriseFrequencyLabel() {
  // Único (WEEKLY/MONTHLY) desativado no painel.
  return 'Mensal';
}

function enterprisePaymentLabel(method) {
  if (method === 'PIX') return 'PIX';
  if (method === 'CREDIT_CARD') return 'Cartão de crédito ou débito';
  return 'Boleto';
}

function EnterpriseCheckoutTotalNote({ totalBrl }) {
  return (
    <div className="mt-6 space-y-3 rounded-xl border border-border bg-muted/20 p-4">
      <p className="text-xs leading-relaxed text-muted-foreground">
        A <strong className="text-foreground">data de vencimento</strong> e o{' '}
        <strong className="text-foreground">valor cobrado</strong> são definidos por eles após o
        pagamento (período proporcional, cupom de parceiro aplicado automaticamente no servidor, etc.). O valor abaixo é
        apenas a <strong className="text-foreground">referência do seu painel</strong> (
        {formatMoneyBRL(ENTERPRISE_INSTANCE_PRICE_BRL)} por instância).
      </p>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm font-medium text-foreground">Referência no painel</span>
        <span className="text-lg font-bold text-foreground">{formatMoneyBRL(totalBrl)}</span>
      </div>
    </div>
  );
}

function WavoipLegalFooter() {
  return (
    <p className="text-center text-xs leading-relaxed text-muted-foreground">
      Ao continuar, você concorda com os{' '}
      <span className="cursor-pointer underline underline-offset-2">Termos de Uso</span> aplicáveis e
      confirma que leu a nossa <span className="cursor-pointer underline underline-offset-2">Política de Privacidade</span>
      .
    </p>
  );
}

function EnterprisePaymentMethodIcon({ method, className }) {
  const base = cn('h-5 w-5 shrink-0', className);
  if (method === 'PIX') return <BoltIcon className={cn(base, 'text-teal-600')} />;
  if (method === 'CREDIT_CARD') return <CreditCardIcon className={cn(base, 'text-sky-600')} />;
  return <BanknotesIcon className={cn(base, 'text-emerald-600')} />;
}

function EnterpriseBillingFormFields({
  billingFullName,
  setBillingFullName,
  billingTaxId,
  setBillingTaxId,
  billingPhone,
  setBillingPhone,
  billingEmail,
  setBillingEmail,
  billingCountry,
  setBillingCountry,
}) {
  return (
    <div className="space-y-4 border-t border-border px-4 pb-4 pt-4">
      <div>
        <Label htmlFor="wavoip-bill-name">Nome completo</Label>
        <Input
          id="wavoip-bill-name"
          className="mt-1.5"
          value={billingFullName}
          onChange={(e) => setBillingFullName(e.target.value)}
          placeholder="Nome ou razão social"
          autoComplete="name"
        />
      </div>
      <div>
        <Label htmlFor="wavoip-bill-tax">CPF/CNPJ</Label>
        <Input
          id="wavoip-bill-tax"
          className="mt-1.5"
          value={billingTaxId}
          onChange={(e) => setBillingTaxId(formatCpfCnpjMask(e.target.value))}
          placeholder="000.000.000-00 ou 00.000.000/0000-00"
          autoComplete="off"
          inputMode="numeric"
        />
      </div>
      <div>
        <Label htmlFor="wavoip-bill-phone">Telefone</Label>
        <Input
          id="wavoip-bill-phone"
          className="mt-1.5"
          value={billingPhone}
          onChange={(e) => setBillingPhone(formatPhoneBrMask(e.target.value))}
          placeholder="(00) 00000-0000"
          autoComplete="tel"
          inputMode="tel"
        />
      </div>
      <div>
        <Label htmlFor="wavoip-bill-email">Email</Label>
        <Input
          id="wavoip-bill-email"
          type="email"
          className="mt-1.5"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          placeholder="seu@email.com"
          autoComplete="email"
        />
      </div>
      <div>
        <Label htmlFor="wavoip-bill-country">País</Label>
        <div className="relative mt-1.5">
          <select
            id="wavoip-bill-country"
            value={billingCountry}
            onChange={(e) => setBillingCountry(e.target.value)}
            className={cn(
              'flex h-10 w-full appearance-none rounded-md border border-input bg-background py-2 pl-10 pr-10 text-sm',
              'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
          >
            <option value="BR">Brasil</option>
          </select>
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base leading-none" aria-hidden>
            🇧🇷
          </span>
          <ChevronDownIcon
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function resolvePainelApiBaseUrl() {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined') {
    const h = window.location?.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'http://localhost:3000';
    }
  }
  return 'https://ia.discador.net';
}

// function resolvePainelApiBaseUrl() {
//   const fromEnv = process.env.REACT_APP_API_BASE_URL;
//   if (fromEnv && String(fromEnv).trim()) {
//     return String(fromEnv).trim().replace(/\/+$/, '');
//   }
//   if (typeof window !== 'undefined') {
//     const h = window.location?.hostname;
//     if (h === 'localhost' || h === '127.0.0.1') {
//       return 'http://localhost:3000';
//     }
//   }
//   return 'http://localhost:3000';
// }

const baseUrl = resolvePainelApiBaseUrl();

/** Só dígitos (para máscaras). */
function digitsOnly(s) {
  return String(s || '').replace(/\D/g, '');
}

/** CPF (até 11 dígitos) ou CNPJ (até 14) com pontuação enquanto digita. */
function formatCpfCnpjMask(raw) {
  const d = digitsOnly(raw).slice(0, 14);
  if (d.length <= 11) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Telefone BR: (DD) NNNNN-NNNN ou (DD) NNNN-NNNN */
function formatPhoneBrMask(raw) {
  const d = digitsOnly(raw).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return d.length === 1 ? `(${d}` : `(${d})`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Converte valor da API em Date no fuso local usando a parte YYYY-MM-DD
 * (evita exibir um dia a menos/mais por UTC em strings tipo ...T00:00:00.000Z).
 */
function parseToLocalDisplayDate(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const y = parseInt(ymd[1], 10);
    const m = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const dt = new Date(y, m, day, 12, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addCalendarMonths(date, n) {
  if (!date || !Number.isFinite(n)) return null;
  return new Date(date.getFullYear(), date.getMonth() + n, date.getDate(), 12, 0, 0);
}

function formatDate(iso) {
  const d = parseToLocalDisplayDate(iso);
  if (!d) return '—';
  try {
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/** Data só (sem hora) — faturas e textos curtos. */
function formatDatePt(value) {
  const d = parseToLocalDisplayDate(value);
  if (!d) return '—';
  try {
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function statusBadgeVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'open') return 'default';
  if (s === 'hibernating') return 'secondary';
  return 'outline';
}

function formatDetailValue(key, value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (/_(date|at)$/i.test(key) || key === 'due_date' || key === 'created_date') {
      return formatDate(value);
    }
    return value;
  }
  return JSON.stringify(value);
}

/** Exibe telefone estilo +55 … quando vier só dígitos (ex.: phone_norm). */
function formatPhoneDisplay(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 10) return `+${digits}`;
  if (digits.startsWith('55') && digits.length >= 12) {
    const d = digits.slice(2);
    const ddd = d.slice(0, 2);
    const rest = d.slice(2);
    if (rest.length === 9) {
      return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    }
    if (rest.length === 8) {
      return `+55 ${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
    }
    return `+55 ${ddd} ${rest}`;
  }
  return `+${digits}`;
}

function maskToken(token) {
  if (!token) return '—';
  const s = String(token);
  if (s.length <= 12) return `${s.slice(0, 4)}…${s.slice(-4)}`;
  return `${s.slice(0, 6)}…${s.slice(-6)}`;
}

/**
 * Host público de dispositivos Wavoip (QR WhatsApp).
 * Ex.: https://devices.wavoip.com/{token}/whatsapp/qr-image
 */
const WAVOIP_DEVICES_BASE =
  (typeof process !== 'undefined' && process.env.REACT_APP_WAVOIP_DEVICES_BASE_URL) ||
  'https://devices.wavoip.com';

function wavoipWhatsAppQrImageUrl(deviceToken) {
  const t = String(deviceToken || '').trim();
  if (!t) return '';
  const base = WAVOIP_DEVICES_BASE.replace(/\/+$/, '');
  return `${base}/${encodeURIComponent(t)}/whatsapp/qr-image`;
}

/** Limite de ligações/mês exibido para plano Free (alinhado ao texto de marketing do painel). */
const FREE_PLAN_MONTHLY_CALL_CAP = 50;

function WavoipWhatsAppLinkHub({
  deviceId,
  token,
  isOpen,
  statusNorm,
  statusFallback,
  displayName,
  phoneDisplay,
  callsMade,
  isFree,
  onRefreshDevice,
  deviceDetailRefreshing,
  onDelete,
  deleteDisabled,
  deleteBusy,
}) {
  const qrBase = wavoipWhatsAppQrImageUrl(token);
  const [bust, setBust] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const qrBlobUrlRef = React.useRef(null);

  useEffect(() => {
    if (!qrBase) {
      if (qrBlobUrlRef.current) {
        URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = null;
      }
      setQrBlobUrl(null);
      setQrLoading(false);
      return undefined;
    }

    if (isOpen) {
      if (qrBlobUrlRef.current) {
        URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = null;
      }
      setQrBlobUrl(null);
      setQrLoading(false);
      return undefined;
    }

    if (deviceId == null || deviceId === '') {
      setQrBlobUrl(null);
      setQrLoading(false);
      setLoadError(false);
      return undefined;
    }

    const headers = getBearerHeaders();
    if (!headers) {
      setLoadError(true);
      setQrLoading(false);
      return undefined;
    }

    let cancelled = false;
    const ac = new AbortController();

    if (qrBlobUrlRef.current) {
      URL.revokeObjectURL(qrBlobUrlRef.current);
      qrBlobUrlRef.current = null;
    }
    setQrBlobUrl(null);
    setQrLoading(true);
    setLoadError(false);

    const url = `${baseUrl}/api/wavoip/devices/${encodeURIComponent(String(deviceId))}/whatsapp-qr-image`;
    const params = bust ? { _: bust } : undefined;

    axios
      .get(url, {
        headers,
        responseType: 'blob',
        params,
        signal: ac.signal,
        timeout: 30000,
        validateStatus: () => true,
      })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status < 200 || res.status >= 300) {
          let msg = `Erro ${res.status} ao carregar o QR.`;
          try {
            const t = await res.data.text();
            const j = JSON.parse(t);
            if (j?.message) msg = j.message;
          } catch {
            /* ignore */
          }
          console.warn('[Wavoip QR]', msg);
          setLoadError(true);
          setQrLoading(false);
          return;
        }
        const blob = res.data;
        if (!(blob instanceof Blob) || blob.size < 64) {
          setLoadError(true);
          setQrLoading(false);
          return;
        }
        const headBuf = await blob.slice(0, 16).arrayBuffer();
        const u8 = new Uint8Array(headBuf);
        const looksPng = u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47;
        const looksJpeg = u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff;
        const looksGif = u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46;
        const looksWebp = u8[8] === 0x57 && u8[9] === 0x45 && u8[10] === 0x42 && u8[11] === 0x50;
        const looksJson = blob.type && String(blob.type).includes('json');
        if (looksJson || (!looksPng && !looksJpeg && !looksGif && !looksWebp)) {
          setLoadError(true);
          setQrLoading(false);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        qrBlobUrlRef.current = objectUrl;
        setQrBlobUrl(objectUrl);
        setLoadError(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setQrBlobUrl(null);
        }
      })
      .finally(() => {
        if (!cancelled) setQrLoading(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
      if (qrBlobUrlRef.current) {
        URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = null;
      }
    };
  }, [deviceId, isOpen, bust, qrBase]);

  if (!qrBase) return null;

  const statusLabel = deviceConnectionLabelPt(statusNorm, statusFallback);
  const title = (displayName || '').trim() || 'Dispositivo';
  const initial = (title.charAt(0) || '?').toUpperCase();
  const callCap = isFree ? FREE_PLAN_MONTHLY_CALL_CAP : null;
  const overCallCap = callCap != null && callsMade > callCap;
  const callBarPct =
    callCap != null && callCap > 0 ? Math.min(100, (callsMade / callCap) * 100) : 0;

  const toolbarClass =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-white shadow-sm transition-colors sm:h-10 sm:w-10';

  return (
    <div className="rounded-2xl border border-border bg-muted/30 p-4 shadow-sm sm:p-6">
      {!isOpen ? (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8">
          <div className="flex shrink-0 justify-center lg:justify-start">
            <div className="rounded-xl border border-border bg-white p-3 shadow-inner">
              {qrLoading ? (
                <div className="flex h-[220px] w-[220px] flex-col items-center justify-center gap-2 text-muted-foreground sm:h-[240px] sm:w-[240px]">
                  <ArrowPathIcon className="h-8 w-8 animate-spin" />
                  <span className="text-xs">Carregando QR…</span>
                </div>
              ) : qrBlobUrl ? (
                <img
                  key={qrBlobUrl}
                  src={qrBlobUrl}
                  alt=""
                  role="presentation"
                  className="h-[220px] w-[220px] max-w-full object-contain sm:h-[240px] sm:w-[240px]"
                  onLoad={(e) => {
                    const { naturalWidth, naturalHeight } = e.currentTarget;
                    if (naturalWidth < 16 || naturalHeight < 16) {
                      setLoadError(true);
                      if (qrBlobUrlRef.current) {
                        URL.revokeObjectURL(qrBlobUrlRef.current);
                        qrBlobUrlRef.current = null;
                      }
                      setQrBlobUrl(null);
                    }
                  }}
                  onError={() => setLoadError(true)}
                />
              ) : loadError ? (
                <div className="flex h-[220px] w-[220px] flex-col items-center justify-center gap-3 bg-muted/50 px-4 text-center text-xs text-muted-foreground sm:h-[240px] sm:w-[240px]">
                  Não foi possível carregar o QR aqui.
                  <a
                    href={qrBase}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'no-underline')}
                  >
                    Abrir imagem do QR
                  </a>
                </div>
              ) : !deviceId ? (
                <img
                  key={`direct-${bust}`}
                  src={bust ? `${qrBase}${qrBase.includes('?') ? '&' : '?'}_=${bust}` : qrBase}
                  alt="QR Code para vincular o WhatsApp"
                  className="h-[220px] w-[220px] max-w-full object-contain sm:h-[240px] sm:w-[240px]"
                  onError={() => setLoadError(true)}
                />
              ) : (
                <div className="flex h-[220px] w-[220px] flex-col items-center justify-center gap-2 text-muted-foreground sm:h-[240px] sm:w-[240px]">
                  <ArrowPathIcon className="h-8 w-8 animate-spin" />
                  <span className="text-xs">Carregando QR…</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-lg font-bold tracking-tight text-foreground">Vincule um número de WhatsApp</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Funciona como o WhatsApp Web: escaneie o QR code para vincular seu número.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={!isFree}
                  title={
                    isFree
                      ? 'Upgrade para planos com mais recursos (em breve no painel).'
                      : 'Seu dispositivo já está em um plano superior ao Free.'
                  }
                  className="gap-1 border-0 bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40"
                >
                  <RocketLaunchIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Upgrade</span>
                </Button>
                <button
                  type="button"
                  title="Atualizar QR code"
                  className={cn(toolbarClass, 'border-emerald-600/80 bg-emerald-600 hover:bg-emerald-700')}
                  onClick={() => {
                    setLoadError(false);
                    setBust(Date.now());
                  }}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  title="Excluir instância"
                  disabled={deleteDisabled}
                  className={cn(
                    toolbarClass,
                    'border-red-600/90 bg-red-600 hover:bg-red-700 disabled:pointer-events-none disabled:opacity-40'
                  )}
                  onClick={onDelete}
                >
                  {deleteBusy ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  ) : (
                    <TrashIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-relaxed text-foreground marker:font-semibold">
              <li>Abra o WhatsApp no seu celular.</li>
              <li>
                Toque em <strong>Mais opções</strong> <span className="whitespace-nowrap">⋮</span> no Android ou em{' '}
                <strong>Configurações</strong> <span className="whitespace-nowrap">⚙️</span> no iPhone.
              </li>
              <li>
                Toque em <strong>Dispositivos conectados</strong> e, em seguida, em{' '}
                <strong>Conectar um dispositivo</strong>.
              </li>
              <li>Aponte seu celular para esta tela para escanear o QR code.</li>
            </ol>

            <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => {
                  setLoadError(false);
                  setBust(Date.now());
                }}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Atualizar QR
              </Button>
              <a
                href={qrBase}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'sm' }),
                  'inline-flex items-center gap-2 no-underline'
                )}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
                Abrir QR em nova aba
              </a>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 text-muted-foreground"
                onClick={onRefreshDevice}
                disabled={deviceDetailRefreshing}
                title="Atualizar status do dispositivo"
              >
                <ArrowPathIcon className={`h-4 w-4 ${deviceDetailRefreshing ? 'animate-spin' : ''}`} />
                Verificar conexão
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
              <span className="text-emerald-600 dark:text-emerald-400">●</span>
              Conectado
            </span>
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!isFree}
                title={
                  isFree
                    ? 'Upgrade para planos com mais recursos (em breve no painel).'
                    : 'Plano Enterprise ativo.'
                }
                className="gap-1 border-0 bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40"
              >
                <RocketLaunchIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Upgrade</span>
              </Button>
              <button
                type="button"
                title="Atualizar dados do dispositivo"
                disabled={deviceDetailRefreshing}
                className={cn(
                  toolbarClass,
                  'border-emerald-600/80 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40'
                )}
                onClick={onRefreshDevice}
              >
                <ArrowPathIcon className={`h-5 w-5 ${deviceDetailRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                title="Desvincular — use o WhatsApp no celular ou aguarde integração no painel"
                disabled
                className={cn(
                  toolbarClass,
                  'cursor-not-allowed border-red-500/50 bg-red-500/70 opacity-50'
                )}
              >
                <LinkSlashIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                title="Excluir instância"
                disabled={deleteDisabled}
                className={cn(
                  toolbarClass,
                  'border-red-600/90 bg-red-600 hover:bg-red-700 disabled:pointer-events-none disabled:opacity-40'
                )}
                onClick={onDelete}
              >
                {deleteBusy ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                ) : (
                  <TrashIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted text-lg font-bold text-foreground ring-2 ring-border">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-semibold text-foreground">{title}</p>
              <p className="truncate font-mono text-sm text-muted-foreground">{phoneDisplay || '—'}</p>
            </div>
          </div>

          {callCap != null ? (
            <div className="mt-5">
              <p className="text-sm text-foreground">
                <span className="tabular-nums font-semibold">{callsMade}</span> de{' '}
                <span className="tabular-nums">{callCap}</span> ligações deste número
                {overCallCap ? (
                  <span className="ml-2 text-xs font-medium text-destructive">(acima do limite do plano Free)</span>
                ) : null}
              </p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    overCallCap ? 'bg-gradient-to-r from-amber-600 to-red-600' : 'bg-gradient-to-r from-emerald-600 to-emerald-400'
                  )}
                  style={{ width: `${callBarPct}%` }}
                />
              </div>
            </div>
          ) : (
            <p className="mt-5 text-sm text-muted-foreground">
              Plano Enterprise · ligações ilimitadas ·{' '}
              <span className="font-medium text-foreground">{callsMade}</span> realizadas neste período
            </p>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            Status: {statusLabel}. Use &quot;Atualizar&quot; se acabou de escanear o QR.
          </p>
        </div>
      )}
    </div>
  );
}

/** Status de conexão do dispositivo (API Wavoip) em português */
function deviceStatusShortPt(raw) {
  const s = String(raw || '').toLowerCase().trim();
  const map = {
    open: 'Conectado',
    hibernating: 'Hibernando',
    closed: 'Desconectado',
    connecting: 'Conectando',
    disconnecting: 'Desconectando',
  };
  return map[s] || (raw ? String(raw) : '—');
}

function deviceConnectionLabelPt(statusNorm, statusFallback) {
  const s = String(statusNorm || statusFallback || '').toLowerCase().trim();
  if (s === 'open') return 'Conectado';
  if (s === 'hibernating') return 'Hibernando';
  if (!s) return 'Status desconhecido';
  return deviceStatusShortPt(s);
}

function callTypePt(raw) {
  const s = String(raw || '').toUpperCase();
  if (s.includes('UNOFFICIAL')) return 'Não oficial';
  if (s.includes('OFFICIAL')) return 'Oficial';
  return raw ? String(raw) : '—';
}

function invoiceFinancialStatusPt(raw) {
  const s = String(raw || '').toUpperCase();
  const map = {
    RECEIVED: 'Recebido',
    CONFIRMED: 'Confirmado',
    WAITING: 'Aguardando pagamento',
    PENDING: 'Pendente',
    OVERDUE: 'Vencido',
    REFUNDED: 'Estornado',
    CANCELLED: 'Cancelado',
    CHARGEBACK: 'Contestação',
    DELETED: 'Excluído',
    EXPIRED: 'Expirado',
    DUNNING_REQUESTED: 'Cobrança solicitada',
    DUNNING_RECEIVED: 'Cobrança recebida',
  };
  return map[s] || (raw ? String(raw) : '—');
}

/** Status de cobrança Asaas (campo status do pagamento) em português curto */
function asaasPaymentStatusPt(raw) {
  const s = String(raw || '').toUpperCase();
  const map = {
    PENDING: 'Pendente',
    RECEIVED: 'Recebido',
    CONFIRMED: 'Confirmado',
    OVERDUE: 'Vencido',
    REFUNDED: 'Estornado',
    CHARGEBACK: 'Contestação',
    CANCELLED: 'Cancelado',
  };
  return map[s] || (raw ? String(raw) : '—');
}

function isAsaasPaymentPaid(status) {
  const s = String(status || '').toUpperCase();
  return s === 'RECEIVED' || s === 'CONFIRMED' || s === 'RECEIVED_IN_CASH';
}

function formatDateShortPt(value) {
  const dt = parseToLocalDisplayDate(value);
  if (!dt || Number.isNaN(dt.getTime())) return '—';
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${String(dt.getDate()).padStart(2, '0')}/${months[dt.getMonth()]}`;
}

/** Fatura Discador.NET (Asaas) + aviso amarelo quando pendente — layout tipo painel Wavoip. */
function DiscadorNetAsaasBillingPanel({ billing, deviceId, isRecorrente, dueDateDisplay, onRefresh, refreshing }) {
  if (!billing?.paymentUrl) {
    return (
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-foreground">
        <p className="font-medium">Cobrança Discador.NET (Asaas) ainda não disponível neste dispositivo.</p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Verifique WAVOIP_ENTERPRISE_ASAAS_BILLING, ASAAS_ACCESS_TOKEN e o retorno da criação da instância.
        </p>
      </div>
    );
  }

  const paid = isAsaasPaymentPaid(billing.status);
  const amount =
    billing.value != null ? billing.value : ENTERPRISE_INSTANCE_PRICE_BRL;
  const invoiceRef = billing.paymentId
    ? `#${String(billing.paymentId).replace(/^pay_/, '').slice(0, 8).toUpperCase()}`
    : `#DISC${deviceId}`;

  if (paid) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Badge variant="secondary">Discador.NET · Asaas</Badge>
            <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              Pagamento confirmado — você já pode vincular o WhatsApp.
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{formatMoneyBRL(amount)}</p>
          </div>
          <a
            href={billing.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'inline-flex shrink-0 items-center gap-2 no-underline'
            )}
          >
            Ver comprovante
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-sm font-semibold text-muted-foreground">{invoiceRef}</span>
          {isRecorrente ? (
            <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Recorrente</span>
          ) : null}
        </div>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">
              Vence em{' '}
              <span className="font-semibold text-foreground">{dueDateDisplay}</span>
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-foreground">
              {formatMoneyBRL(amount)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">Discador.NET · Asaas</p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Aberto
          </span>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <a
            href={billing.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ variant: 'default', size: 'sm' }),
              'inline-flex flex-1 items-center justify-center no-underline sm:flex-none'
            )}
          >
            Pagar fatura
          </a>
          <a
            href={billing.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'inline-flex flex-1 items-center justify-center no-underline sm:flex-none'
            )}
          >
            Ver detalhes
          </a>
          {onRefresh ? (
            <Button type="button" variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing} className="gap-1">
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center rounded-2xl border border-amber-400/50 bg-amber-50 p-6 text-center shadow-sm dark:border-amber-500/40 dark:bg-amber-500/10">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/30 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300">
          <ExclamationTriangleIcon className="h-8 w-8" />
        </span>
        <p className="text-lg font-bold text-amber-800 dark:text-amber-200">Pagamento pendente</p>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-amber-900/90 dark:text-amber-100/90">
          {DISCADOR_PAYMENT_PENDING_LEGEND}
        </p>
        <p className="mt-4 text-xs font-medium text-amber-800/80 dark:text-amber-200/80">
          A vinculação do WhatsApp (QR Code) ficará disponível após a confirmação do pagamento.
        </p>
      </div>
    </div>
  );
}

const DETAIL_SECTION_GERAL = 'geral';
const DETAIL_SECTION_FINANCEIRO = 'financeiro';
const DETAIL_SECTION_SIP = 'sip';

const FINANCEIRO_TAB_TODAS = 'todas';
const FINANCEIRO_TAB_ABERTAS = 'abertas';
const FINANCEIRO_TAB_VENCIDAS = 'vencidas';
const FINANCEIRO_TAB_PAGAS = 'pagas';
const FINANCEIRO_TAB_CANCELADAS = 'canceladas';

function financeiroStatusKey(raw) {
  return String(raw || '').toUpperCase();
}

function isFinanceiroPaid(status) {
  const s = financeiroStatusKey(status);
  return s === 'RECEIVED' || s === 'CONFIRMED' || s === 'RECEIVED_IN_CASH';
}

function isFinanceiroCancelled(status) {
  const s = financeiroStatusKey(status);
  return ['CANCELLED', 'REFUNDED', 'DELETED', 'CHARGEBACK', 'EXPIRED'].includes(s);
}

function isFinanceiroOverdue(status) {
  return financeiroStatusKey(status) === 'OVERDUE';
}

function isFinanceiroOpen(status) {
  const s = financeiroStatusKey(status);
  if (isFinanceiroPaid(s) || isFinanceiroCancelled(s)) return false;
  return ['PENDING', 'WAITING', 'OVERDUE', 'AWAITING_RISK_ANALYSIS'].includes(s) || !s;
}

function financeiroStatusLabelPt(status) {
  const s = financeiroStatusKey(status);
  if (isFinanceiroPaid(s)) return 'Pago';
  if (s === 'OVERDUE') return 'Vencido';
  if (isFinanceiroCancelled(s)) return invoiceFinancialStatusPt(s);
  return 'Aguardando pagamento';
}

function financeiroStatusDotClass(status) {
  if (isFinanceiroPaid(status)) return 'bg-emerald-500';
  if (isFinanceiroOverdue(status)) return 'bg-red-500';
  if (isFinanceiroCancelled(status)) return 'bg-muted-foreground';
  return 'bg-amber-400';
}

function buildFinanceiroInvoiceRows({
  usesDiscadorAsaas,
  discadorBilling,
  instanceInvoices,
  isRecorrente,
  dueDateDisplay,
  deviceId,
}) {
  if (usesDiscadorAsaas && discadorBilling?.paymentUrl) {
    const pid = discadorBilling.paymentId ? String(discadorBilling.paymentId) : '';
    return [
      {
        rowKey: pid || `discador-${deviceId}`,
        id: pid ? pid.replace(/^pay_/, '').slice(-8).toUpperCase() : String(deviceId),
        status: discadorBilling.status || 'PENDING',
        tipo: isRecorrente ? 'Recorrente' : 'Avulso',
        validade: dueDateDisplay,
        valor: discadorBilling.value != null ? discadorBilling.value : ENTERPRISE_INSTANCE_PRICE_BRL,
        payUrl: discadorBilling.paymentUrl,
        source: 'discador',
      },
    ];
  }
  return (instanceInvoices || []).map((inv) => ({
    rowKey: `wavoip-${inv.id}`,
    id: String(inv.id),
    status: inv.status,
    tipo:
      String(inv.cycle || '').toUpperCase() === 'MONTHLY' || isRecorrente
        ? 'Recorrente'
        : billingCyclePt(inv.cycle),
    validade: formatDatePt(inv.due_date),
    valor: inv.total,
    payUrl: inv.invoice_url,
    source: 'wavoip',
  }));
}

function filterFinanceiroRows(rows, tab) {
  if (tab === FINANCEIRO_TAB_TODAS) return rows;
  if (tab === FINANCEIRO_TAB_ABERTAS) return rows.filter((r) => isFinanceiroOpen(r.status));
  if (tab === FINANCEIRO_TAB_VENCIDAS) return rows.filter((r) => isFinanceiroOverdue(r.status));
  if (tab === FINANCEIRO_TAB_PAGAS) return rows.filter((r) => isFinanceiroPaid(r.status));
  if (tab === FINANCEIRO_TAB_CANCELADAS) return rows.filter((r) => isFinanceiroCancelled(r.status));
  return rows;
}

/** Menu lateral do modal da instância (Financeiro, SIP). */
function InstanceDetailSideMenu({ section, onSectionChange }) {
  const items = [
    { id: DETAIL_SECTION_GERAL, label: 'Visão geral' },
    { id: DETAIL_SECTION_FINANCEIRO, label: 'Financeiro' },
    { id: DETAIL_SECTION_SIP, label: 'SIP' },
  ];
  return (
    <nav className="w-full shrink-0 border-b border-border pb-4 sm:w-44 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-4">
      <p className="mb-3 text-sm font-bold text-foreground">Menu</p>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onSectionChange(item.id)}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
                section === item.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              )}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Financeiro da instância — layout tipo painel Wavoip (banner, cards, tabela). */
function InstanceFinanceiroPanel({
  rows,
  usesDiscadorAsaas,
  discadorBilling,
  isRecorrente,
  invoicesLoading,
  invoicesError,
  onRefresh,
  refreshing,
}) {
  const [tab, setTab] = useState(FINANCEIRO_TAB_TODAS);
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const filtered = filterFinanceiroRows(rows, tab);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pendingCount = rows.filter((r) => isFinanceiroOpen(r.status)).length;
  const subscriptionPending =
    rows.length === 0
      ? true
      : rows.some((r) => isFinanceiroOpen(r.status));
  const primaryPending = rows.find((r) => isFinanceiroOpen(r.status) && r.payUrl);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [tab, rows.length]);

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllPage = () => {
    const keys = pageRows.map((r) => r.rowKey);
    const allOnPage = keys.length > 0 && keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPage) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
        <p>
          <span className="font-semibold">Atenção:</span> Essas faturas são do dispositivo atual
          {usesDiscadorAsaas ? ' (cobrança Discador.NET · Asaas)' : ''}.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-muted/40 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assinatura</p>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={onRefresh}
              disabled={refreshing}
              title="Atualizar"
            >
              <ArrowPathIcon className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </button>
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-foreground">
            {subscriptionPending ? 'PENDENTE' : 'ATIVA'}
          </p>
          {primaryPending?.payUrl ? (
            <a
              href={primaryPending.payUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-sm font-medium text-primary underline-offset-2 hover:underline"
            >
              Pagar fatura
            </a>
          ) : null}
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Total de faturas
            </p>
            <CurrencyDollarIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{rows.length}</p>
          <p className="mt-1 text-xs text-muted-foreground">Quantidade de faturas neste dispositivo</p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Faturas a pagar
            </p>
            <CreditCardIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{pendingCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">Quantidade de faturas pendentes</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-muted/30 shadow-sm">
        <div className="flex flex-col gap-3 border-b border-border bg-muted/50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1">
            {[
              [FINANCEIRO_TAB_TODAS, 'Todas'],
              [FINANCEIRO_TAB_ABERTAS, 'Faturas Abertas'],
              [FINANCEIRO_TAB_VENCIDAS, 'Faturas Vencidas'],
              [FINANCEIRO_TAB_PAGAS, 'Faturas Pagas'],
              [FINANCEIRO_TAB_CANCELADAS, 'Faturas Canceladas'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  tab === id
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" className="gap-2 shrink-0" disabled>
            <CalendarDaysIcon className="h-4 w-4" />
            Desde o início
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </Button>
        </div>

        {invoicesError ? (
          <p className="px-4 py-6 text-sm text-destructive">{invoicesError}</p>
        ) : null}
        {invoicesLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
            Carregando faturas…
          </div>
        ) : null}

        {!invoicesLoading && (
          <div className="overflow-x-auto bg-card">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-border bg-muted/20 text-xs font-semibold uppercase text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      className="rounded border-border"
                      checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.rowKey))}
                      onChange={toggleAllPage}
                      aria-label="Selecionar página"
                    />
                  </th>
                  <th className="px-3 py-3">ID</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Tipo</th>
                  <th className="px-3 py-3">Validade</th>
                  <th className="px-3 py-3">Valor</th>
                  <th className="px-3 py-3 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      {rows.length === 0
                        ? usesDiscadorAsaas && !discadorBilling?.paymentUrl
                          ? 'Nenhuma cobrança Discador.NET disponível para este dispositivo.'
                          : 'Nenhuma fatura neste filtro.'
                        : 'Nenhuma fatura neste filtro.'}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr key={row.rowKey} className="hover:bg-muted/20">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-border"
                          checked={selected.has(row.rowKey)}
                          onChange={() => toggleRow(row.rowKey)}
                        />
                      </td>
                      <td className="px-3 py-3 font-medium text-foreground">{row.id}</td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={cn('h-2 w-2 shrink-0 rounded-full', financeiroStatusDotClass(row.status))}
                          />
                          {financeiroStatusLabelPt(row.status)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{row.tipo}</td>
                      <td className="px-3 py-3 text-muted-foreground">{row.validade}</td>
                      <td className="px-3 py-3 font-semibold tabular-nums text-foreground">
                        {formatMoneyBRL(row.valor)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {row.payUrl && isFinanceiroOpen(row.status) ? (
                          <a
                            href={row.payUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              buttonVariants({ variant: 'default', size: 'sm' }),
                              'inline-flex no-underline'
                            )}
                          >
                            Pagar
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>
            {selected.size} de {filtered.length} fatura(s) selecionadas.
          </span>
          <div className="flex items-center gap-2">
            <span>
              Página {safePage} de {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={safePage <= 1}
              onClick={() => setPage(1)}
            >
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={safePage >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SipCredentialRow({ label, value, secret = false, highlight = null }) {
  const [visible, setVisible] = useState(!secret);
  const display = value || '—';
  const shown = secret && !visible ? '••••••••••••••••' : display;

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success('Copiado.');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <div className="border-b border-border py-4 last:border-b-0">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code
          className={cn(
            'min-w-0 flex-1 break-all rounded-md px-3 py-2 font-mono text-sm',
            highlight === 'user' && 'bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100',
            highlight === 'password' && 'bg-yellow-100 text-yellow-950 dark:bg-yellow-500/20 dark:text-yellow-100',
            !highlight && 'bg-muted text-foreground'
          )}
        >
          {shown}
        </code>
        <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={copy} disabled={!value} title="Copiar">
          <ClipboardDocumentIcon className="h-4 w-4" />
        </Button>
        {secret ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => setVisible((v) => !v)}
            title={visible ? 'Ocultar' : 'Mostrar'}
          >
            {visible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** SIP — credenciais (token) + roteamento via GET/PUT settings Wavoip. */
function InstanceSipPanel({ deviceId, tokenStr }) {
  const [sipHost, setSipHost] = useState(WAVOIP_SIP_HOST_DEFAULT);
  const [callerId, setCallerId] = useState('');
  const [sipIp, setSipIp] = useState('');
  const [sipPort, setSipPort] = useState('');
  const [transport, setTransport] = useState('UDP');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadSettings = useCallback(async () => {
    if (deviceId == null || deviceId === '') return;
    const headers = getBearerHeaders();
    if (!headers) {
      setLoadError('Sessão inválida.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(
        `${baseUrl}/api/wavoip/devices/${encodeURIComponent(String(deviceId))}/settings`,
        { headers }
      );
      const host = data?._painel?.sipHost || WAVOIP_SIP_HOST_DEFAULT;
      setSipHost(host);
      const sip = data?.sip || {};
      setCallerId(sip.caller_id != null ? String(sip.caller_id) : '');
      setSipIp(sip.ip != null ? String(sip.ip) : '');
      setSipPort(sip.port != null && sip.port !== '' ? String(sip.port) : '');
      const tr = String(sip.transport || 'UDP').toUpperCase();
      setTransport(SIP_TRANSPORT_OPTIONS.includes(tr) ? tr : 'UDP');
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message || 'Erro ao carregar SIP.');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    const headers = getBearerHeaders();
    if (!headers) {
      toast.error('Sessão inválida.');
      return;
    }
    const portTrim = String(sipPort || '').trim();
    let portVal = null;
    if (portTrim) {
      const n = parseInt(portTrim, 10);
      if (!Number.isFinite(n) || n < 1 || n > 65535) {
        toast.error('Porta inválida (use 1–65535).');
        return;
      }
      portVal = n;
    }

    setSaving(true);
    try {
      await axios.put(
        `${baseUrl}/api/wavoip/devices/${encodeURIComponent(String(deviceId))}/settings`,
        {
          sip: {
            caller_id: String(callerId || '').trim() || null,
            ip: String(sipIp || '').trim() || null,
            port: portVal,
            transport: transport || 'UDP',
          },
        },
        { headers }
      );
      toast.success('Configurações SIP salvas.');
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erro ao salvar SIP.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center gap-2 text-muted-foreground">
        <ArrowPathIcon className="h-5 w-5 animate-spin" />
        Carregando SIP…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {loadError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {loadError}
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={loadSettings}>
            Tentar novamente
          </Button>
        </div>
      ) : null}

      <section>
        <h3 className="text-lg font-semibold text-foreground">Autenticação</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Utilize essas credenciais para registrar seu SIP Trunk. Usuário e senha são sempre o token deste
          dispositivo.
        </p>
        <div className="mt-4 rounded-xl border border-border bg-card px-4">
          <SipCredentialRow label="Host" value={sipHost} />
          <SipCredentialRow label="Usuário" value={tokenStr} secret highlight="user" />
          <SipCredentialRow label="Senha" value={tokenStr} secret highlight="password" />
        </div>
      </section>

      <section className="border-t border-border pt-8">
        <h3 className="text-lg font-semibold text-foreground">Configurações Sip</h3>
        <p className="mt-1 text-sm font-medium text-muted-foreground">Roteamento</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="wavoip-sip-caller-id">Caller_ID</Label>
            <Input
              id="wavoip-sip-caller-id"
              className="mt-1.5 bg-muted/40"
              placeholder="Digite o Caller ID"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="wavoip-sip-ip">IP</Label>
            <Input
              id="wavoip-sip-ip"
              className="mt-1.5 bg-muted/40"
              placeholder="Digite o IP"
              value={sipIp}
              onChange={(e) => setSipIp(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="wavoip-sip-port">Porta</Label>
            <Input
              id="wavoip-sip-port"
              className="mt-1.5 bg-muted/40"
              placeholder="Ex: 5060"
              inputMode="numeric"
              value={sipPort}
              onChange={(e) => setSipPort(e.target.value.replace(/\D/g, ''))}
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="wavoip-sip-transport">Transporte</Label>
            <select
              id="wavoip-sip-transport"
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={transport}
              onChange={(e) => setTransport(e.target.value)}
            >
              {SIP_TRANSPORT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="button" onClick={saveSettings} disabled={saving || !!loadError}>
            {saving ? (
              <>
                <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </section>
    </div>
  );
}

function invoiceStatusBadgeVariant(raw) {
  const s = String(raw || '').toUpperCase();
  if (s === 'RECEIVED' || s === 'CONFIRMED') return 'default';
  if (s === 'WAITING' || s === 'PENDING') return 'secondary';
  if (s === 'OVERDUE' || s === 'CHARGEBACK') return 'destructive';
  return 'outline';
}

function billingCyclePt(raw) {
  if (!raw) return '—';
  const s = String(raw).toUpperCase();
  const map = {
    MONTHLY: 'Mensal',
    YEARLY: 'Anual',
    WEEKLY: 'Semanal',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
  };
  return map[s] || String(raw);
}

function formatMoneyBRL(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDetailValuePt(key, value) {
  const k = String(key).toLowerCase();
  if (k === 'status' || k === 'status_norm') return deviceStatusShortPt(value);
  if (k === 'call_type') return callTypePt(value);
  return formatDetailValue(key, value);
}

export default function WavoipInstanciasWhatsApp() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailDevice, setDetailDevice] = useState(null);
  const [detailFetchId, setDetailFetchId] = useState(null);
  const [tokenVisible, setTokenVisible] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [wavoipInvoices, setWavoipInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);
  const [detailMenuSection, setDetailMenuSection] = useState(DETAIL_SECTION_GERAL);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPlan, setCreatePlan] = useState(PLAN_WHATSAPP_ENTERPRISE);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createFlowStep, setCreateFlowStep] = useState(CREATE_FLOW_PLAN);
  const [enterpriseBillingType, setEnterpriseBillingType] = useState(BILLING_RECURRENT);
  const [enterpriseQuantity, setEnterpriseQuantity] = useState(1);
  const [enterpriseCycle, setEnterpriseCycle] = useState('MONTHLY');
  const [enterprisePaymentMethod, setEnterprisePaymentMethod] = useState('PIX');
  const [billingFullName, setBillingFullName] = useState('');
  const [billingTaxId, setBillingTaxId] = useState('');
  const [billingPhone, setBillingPhone] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingCountry, setBillingCountry] = useState('BR');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setTokenVisible(false);
  }, [detailFetchId, detailDevice?.id]);

  useEffect(() => {
    if (detailDevice && typeof detailDevice.name === 'string') {
      setEditName(detailDevice.name);
    } else if (detailDevice) {
      setEditName('');
    }
  }, [detailDevice]);

  const load = useCallback(async () => {
    const headers = getBearerHeaders();
    if (!headers) {
      setError('Sessão inválida. Faça login novamente.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${baseUrl}/api/wavoip/devices/me`, { headers });
      if (data?.type === 'success' && Array.isArray(data.result)) {
        setDevices(data.result);
      } else {
        setDevices([]);
        setError('Resposta inesperada do servidor.');
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        'Não foi possível carregar as instâncias.';
      setError(msg);
      setDevices([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyToken = async (token, id) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success(`Token do dispositivo ${id} copiado.`);
    } catch {
      toast.error('Não foi possível copiar o token.');
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailDevice(null);
    setDetailError(null);
    setDetailFetchId(null);
    setDetailRefreshing(false);
    setEditName('');
    setSavingName(false);
    setWavoipInvoices([]);
    setInvoicesError(null);
    setInvoicesLoading(false);
    setDetailMenuSection(DETAIL_SECTION_GERAL);
  };

  const deleteInstance = async (deviceId, { closeModal = false } = {}) => {
    if (deviceId == null || deviceId === '') return;
    if (!window.confirm(DELETE_CONFIRM_MSG)) return;
    const headers = getBearerHeaders();
    if (!headers) {
      toast.error('Sessão inválida.');
      return;
    }
    const idStr = String(deviceId);
    setDeletingId(idStr);
    try {
      const { data } = await axios.delete(
        `${baseUrl}/api/wavoip/devices/${encodeURIComponent(idStr)}`,
        { headers }
      );
      if (data?.type === 'success') {
        const msg =
          typeof data?.result === 'string' ? data.result : 'Instância excluída com sucesso.';
        toast.success(msg);
        if (closeModal) closeDetail();
        await load();
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (e) {
      const msg =
        e.response?.data?.message || e.message || 'Não foi possível excluir a instância.';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const loadDetailModalData = useCallback(
    async (id, { initial = false } = {}) => {
      const headers = getBearerHeaders();
      if (!headers) {
        if (initial) toast.error('Sessão inválida.');
        return;
      }
      if (initial) {
        setDetailLoading(true);
        setDetailDevice(null);
        setDetailError(null);
        setWavoipInvoices([]);
        setInvoicesError(null);
      } else {
        setDetailRefreshing(true);
      }
      setInvoicesLoading(true);
      try {
        const [dRes, iRes] = await Promise.allSettled([
          axios.get(`${baseUrl}/api/wavoip/devices/${encodeURIComponent(String(id))}`, { headers }),
          axios.get(`${baseUrl}/api/wavoip/financial/invoices/me`, { headers }),
        ]);

        if (dRes.status === 'fulfilled') {
          const data = dRes.value.data;
          if (data?.type === 'success' && Array.isArray(data.result) && data.result.length > 0) {
            setDetailDevice(data.result[0]);
            setDetailError(null);
          } else {
            setDetailError('Resposta inesperada do servidor.');
          }
        } else {
          const err = dRes.reason;
          const msg =
            err?.response?.data?.message || err?.message || 'Não foi possível carregar os detalhes.';
          setDetailError(msg);
          if (initial) toast.error(msg);
        }

        if (iRes.status === 'fulfilled') {
          const data = iRes.value.data;
          if (data?.type === 'success' && Array.isArray(data.result)) {
            setWavoipInvoices(data.result);
            setInvoicesError(null);
          } else {
            setInvoicesError('Resposta inesperada ao carregar faturas.');
          }
        } else {
          setInvoicesError('Não foi possível carregar as faturas.');
        }
      } finally {
        setDetailLoading(false);
        setDetailRefreshing(false);
        setInvoicesLoading(false);
      }
    },
    []
  );

  const openDeviceDetail = async (id) => {
    setDetailOpen(true);
    setDetailMenuSection(DETAIL_SECTION_GERAL);
    setDetailFetchId(id);
    await loadDetailModalData(id, { initial: true });
  };

  const refreshDeviceDetail = () => {
    if (detailFetchId == null) return;
    loadDetailModalData(detailFetchId, { initial: false });
  };

  const openCreateModal = () => {
    setCreatePlan(PLAN_WHATSAPP_ENTERPRISE);
    setCreateFlowStep(CREATE_FLOW_PLAN);
    setEnterpriseBillingType(BILLING_RECURRENT);
    setEnterpriseQuantity(1);
    setEnterpriseCycle('MONTHLY');
    setEnterprisePaymentMethod('PIX');
    setBillingFullName('');
    setBillingTaxId('');
    setBillingPhone('');
    setBillingEmail('');
    setBillingCountry('BR');
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateSubmitting(false);
    setCreateFlowStep(CREATE_FLOW_PLAN);
    setEnterpriseBillingType(BILLING_RECURRENT);
    setEnterpriseQuantity(1);
    setEnterpriseCycle('MONTHLY');
    setEnterprisePaymentMethod('PIX');
    setBillingFullName('');
    setBillingTaxId('');
    setBillingPhone('');
    setBillingEmail('');
    setBillingCountry('BR');
  };

  const goBackCreateFlow = () => {
    if (createFlowStep === CREATE_FLOW_ENTERPRISE_FINALIZE) {
      setCreateFlowStep(CREATE_FLOW_ENTERPRISE_PAYMENT);
      return;
    }
    if (createFlowStep === CREATE_FLOW_ENTERPRISE_PAYMENT) {
      setCreateFlowStep(CREATE_FLOW_ENTERPRISE_CHECKOUT);
      return;
    }
    if (createFlowStep === CREATE_FLOW_ENTERPRISE_CHECKOUT) {
      setCreateFlowStep(CREATE_FLOW_PLAN);
    }
  };

  const submitCreateFree = async () => {
    const headers = getBearerHeaders();
    if (!headers) {
      toast.error('Sessão inválida.');
      return;
    }
    setCreateSubmitting(true);
    try {
      const { data } = await axios.post(
        `${baseUrl}/api/wavoip/devices`,
        { plan: PLAN_WHATSAPP_FREE },
        { headers }
      );
      if (data?.type === 'success') {
        const msg =
          typeof data?.result === 'string' ? data.result : 'Instância criada com sucesso.';
        toast.success(msg);
        closeCreateModal();
        await load();
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        'Não foi possível criar a instância. Se o erro persistir, confira o endpoint(variável WAVOIP_CREATE_DEVICE_PATH no backend).';
      toast.error(msg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleAdvanceFromPayment = () => {
    if (!billingFullName.trim() || !billingTaxId.trim() || !billingPhone.trim() || !billingEmail.trim()) {
      toast.error('Preencha nome completo, CPF/CNPJ, telefone e e-mail.');
      return;
    }
    const em = billingEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setCreateFlowStep(CREATE_FLOW_ENTERPRISE_FINALIZE);
  };

  const submitCreateEnterprise = async () => {
    const headers = getBearerHeaders();
    if (!headers) {
      toast.error('Sessão inválida.');
      return;
    }
    if (!billingFullName.trim() || !billingTaxId.trim() || !billingPhone.trim() || !billingEmail.trim()) {
      toast.error('Preencha nome completo, CPF/CNPJ, telefone e e-mail.');
      return;
    }
    const em = billingEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setCreateSubmitting(true);
    try {
      const payload = {
        plan: PLAN_WHATSAPP_ENTERPRISE,
        enterprise: {
          billingType: enterpriseBillingType,
          quantity: enterpriseQuantity,
          cycle: enterpriseCycle,
          payment_method: enterprisePaymentMethod,
        },
        enterpriseBilling: {
          fullName: billingFullName.trim(),
          taxId: billingTaxId.trim(),
          phone: billingPhone.trim(),
          email: billingEmail.trim(),
          country: billingCountry,
        },
      };
      const { data } = await axios.post(`${baseUrl}/api/wavoip/devices`, payload, { headers });
      if (data?.type === 'success') {
        const msg =
          typeof data?.result === 'string' ? data.result : 'Instância criada com sucesso.';
        toast.success(msg);
        const asaasPanel = data._painel?.asaas;
        if (asaasPanel?.error) {
          toast.error(
            `Instância criada, mas a cobrança no Asaas falhou: ${asaasPanel.error}. Abra os detalhes da instância ou contate o suporte.`,
            { duration: 12000 }
          );
        } else if (asaasPanel && !asaasPanel.skipped && asaasPanel.asaasPaymentUrl) {
          toast.success(
            (t) => (
              <div className="text-sm">
                <p className="font-medium text-foreground">Cobrança Discador.NET (Asaas)</p>
                <p className="mt-1 text-muted-foreground">
                  Valor: {formatMoneyBRL(asaasPanel.asaasChargeValue)} — use o link do seu Asaas para pagar.
                </p>
                <a
                  href={asaasPanel.asaasPaymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 font-medium text-primary underline"
                  onClick={() => toast.dismiss(t.id)}
                >
                  Abrir link de pagamento
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>
            ),
            { duration: 16000 }
          );
        }
        closeCreateModal();
        await load();
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        'Não foi possível criar a instância Enterprise. Verifique os dados e tente novamente.';
      toast.error(msg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handlePrimaryPlanStep = () => {
    if (createPlan === PLAN_WHATSAPP_FREE) {
      submitCreateFree();
      return;
    }
    setCreateFlowStep(CREATE_FLOW_ENTERPRISE_CHECKOUT);
  };

  const saveDeviceName = async () => {
    if (detailFetchId == null || !editName.trim()) {
      toast.error('Informe um nome válido.');
      return;
    }
    const headers = getBearerHeaders();
    if (!headers) {
      toast.error('Sessão inválida.');
      return;
    }
    setSavingName(true);
    try {
      const { data } = await axios.put(
        `${baseUrl}/api/wavoip/devices/${encodeURIComponent(String(detailFetchId))}`,
        { name: editName.trim() },
        { headers }
      );
      const ok = data?.type === 'success';
      const msg =
        typeof data?.result === 'string' ? data.result : ok ? 'Dispositivo atualizado.' : null;
      if (ok) {
        toast.success(msg || 'Dispositivo atualizado com sucesso.');
        setDevices((prev) =>
          prev.map((row) =>
            row.id === detailFetchId ? { ...row, name: editName.trim() } : row
          )
        );
        await loadDetailModalData(detailFetchId, { initial: false });
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (e) {
      const msg =
        e.response?.data?.message || e.message || 'Não foi possível salvar o nome.';
      toast.error(msg);
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-lg bg-primary/10 p-2 text-primary">
            <ServerStackIcon className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Instâncias WhatsApp (Ligação)
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Dispositivos vinculados à conta Wavoip do sistema. A autenticação ocorre ao abrir
              esta página.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button type="button" onClick={openCreateModal} className="gap-2">
            <PlusCircleIcon className="h-4 w-4" />
            Nova instância
          </Button>
          <Button type="button" variant="outline" onClick={load} disabled={loading} className="gap-2">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-background/80 p-4 py-8 backdrop-blur sm:items-center sm:py-4">
          <Card
            className={cn(
              'relative flex max-h-[min(92vh,900px)] min-h-0 w-full flex-col overflow-hidden border-border shadow-xl',
              createFlowStep === CREATE_FLOW_PLAN ? 'max-w-3xl' : 'max-w-lg'
            )}
          >
            {createFlowStep !== CREATE_FLOW_PLAN ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute left-3 top-3 z-10"
                onClick={goBackCreateFlow}
                aria-label="Voltar"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-3 top-3 z-10"
              onClick={closeCreateModal}
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>

            <CardContent className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-6 pt-14 sm:p-8 sm:pt-16">
              {createFlowStep === CREATE_FLOW_PLAN ? (
                <>
                  <h2 className="text-2xl font-bold tracking-tight text-foreground">Escolha o tipo de dispositivo</h2>
                  <p className="mt-2 text-sm text-muted-foreground">Selecione o tipo de plano:</p>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={createPlan === PLAN_WHATSAPP_FREE}
                      onClick={() => setCreatePlan(PLAN_WHATSAPP_FREE)}
                      className={cn(
                        'relative rounded-2xl border-2 p-5 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        createPlan === PLAN_WHATSAPP_FREE
                          ? 'border-sky-500 bg-sky-500/5 shadow-sm ring-2 ring-sky-500/30'
                          : 'border-border bg-card hover:border-muted-foreground/30'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-emerald-600">
                            <HomeIcon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold leading-tight text-foreground">Whatsapp Desktop Gratuito</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">Para pequenas empresas</p>
                          </div>
                        </div>
                        <span
                          className={cn(
                            'mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                            createPlan === PLAN_WHATSAPP_FREE
                              ? 'border-sky-600 bg-sky-600'
                              : 'border-muted-foreground/40 bg-background'
                          )}
                          aria-hidden
                        >
                          {createPlan === PLAN_WHATSAPP_FREE ? (
                            <span className="h-2 w-2 rounded-full bg-white" />
                          ) : null}
                        </span>
                      </div>
                      <p className="mt-5 text-3xl font-bold text-foreground">Grátis</p>
                      <ul className="mt-4 space-y-2.5 text-sm text-foreground">
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                          Até 50 ligações/mês
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Limite de 1 ligação simultânea por QR Code
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Faz ligações
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Recebe ligações
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Gravação das chamadas
                        </li>
                      </ul>
                    </button>

                    <button
                      type="button"
                      role="radio"
                      aria-checked={createPlan === PLAN_WHATSAPP_ENTERPRISE}
                      onClick={() => setCreatePlan(PLAN_WHATSAPP_ENTERPRISE)}
                      className={cn(
                        'relative rounded-2xl border-2 p-5 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        createPlan === PLAN_WHATSAPP_ENTERPRISE
                          ? 'border-sky-500 bg-sky-500/5 shadow-sm ring-2 ring-sky-500/30'
                          : 'border-border bg-card hover:border-muted-foreground/30'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-sky-600">
                            <BuildingOffice2Icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold leading-tight text-foreground">Whatsapp Desktop Enterprise</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">Para médias empresas</p>
                          </div>
                        </div>
                        <span
                          className={cn(
                            'mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                            createPlan === PLAN_WHATSAPP_ENTERPRISE
                              ? 'border-sky-600 bg-sky-600'
                              : 'border-muted-foreground/40 bg-background'
                          )}
                          aria-hidden
                        >
                          {createPlan === PLAN_WHATSAPP_ENTERPRISE ? (
                            <span className="h-2 w-2 rounded-full bg-white" />
                          ) : null}
                        </span>
                      </div>
                      <p className="mt-5 text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                        {formatMoneyBRL(ENTERPRISE_INSTANCE_PRICE_BRL)}&nbsp;/instância
                      </p>
                      <ul className="mt-4 space-y-2.5 text-sm text-foreground">
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Ligações ilimitadas
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Limite de 1 ligação simultânea por QR Code
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Faz ligações
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Recebe ligações
                        </li>
                        <li className="flex gap-2">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          Gravação das chamadas
                        </li>
                      </ul>
                    </button>
                  </div>

                  <div className="mt-8 flex flex-wrap justify-end gap-2">
                    <Button type="button" variant="outline" onClick={closeCreateModal} disabled={createSubmitting}>
                      Cancelar
                    </Button>
                    <Button type="button" onClick={handlePrimaryPlanStep} disabled={createSubmitting}>
                      {createSubmitting ? (
                        <>
                          <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                          Criando…
                        </>
                      ) : createPlan === PLAN_WHATSAPP_FREE ? (
                        'Criar instância'
                      ) : (
                        'Continuar'
                      )}
                    </Button>
                  </div>

                  <p className="mt-4 text-xs text-muted-foreground">
                    Free: envia <span className="font-mono">type: FREE</span> para a Wavoip. Enterprise: cobrança
                    recorrente mensal, quantidade e forma de pagamento; o cupom de parceiro é aplicado automaticamente no
                    servidor.
                  </p>
                </>
              ) : null}

              {createFlowStep === CREATE_FLOW_ENTERPRISE_CHECKOUT ? (
                <>
                  <h2 className="text-center text-xl font-bold tracking-tight text-foreground">
                    Escolha como deseja pagar
                  </h2>
                  <p className="mt-1 text-center text-sm text-muted-foreground">WhatsApp Desktop Enterprise</p>

                  <div className="mt-6">
                    <div className="w-full rounded-xl border-2 border-sky-500 bg-sky-500/5 px-4 py-3 text-center text-sm font-semibold text-foreground ring-2 ring-sky-500/25">
                      Recorrente
                    </div>
                    {/*
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setEnterpriseBillingType(BILLING_RECURRENT)}
                        className={cn(
                          'rounded-xl border-2 px-4 py-3 text-center text-sm font-semibold transition-all',
                          enterpriseBillingType === BILLING_RECURRENT
                            ? 'border-sky-500 bg-sky-500/5 text-foreground ring-2 ring-sky-500/25'
                            : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40'
                        )}
                      >
                        Recorrente
                      </button>
                      <button
                        type="button"
                        onClick={() => setEnterpriseBillingType(BILLING_UNIQUE)}
                        className={cn(
                          'rounded-xl border-2 px-4 py-3 text-center text-sm font-semibold transition-all',
                          enterpriseBillingType === BILLING_UNIQUE
                            ? 'border-sky-500 bg-sky-500/5 text-foreground ring-2 ring-sky-500/25'
                            : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40'
                        )}
                      >
                        Único
                      </button>
                    </div>
                    */}
                  </div>

                  <div className="mt-6">
                    <Label className="text-sm font-medium">Quantidade</Label>
                    <div className="mt-2 flex items-center justify-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setEnterpriseQuantity((q) => Math.max(1, q - 1))}
                        disabled={enterpriseQuantity <= 1}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </Button>
                      <div className="min-w-[3rem] rounded-lg border border-border bg-muted/30 py-2 text-center text-lg font-semibold tabular-nums">
                        {enterpriseQuantity}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setEnterpriseQuantity((q) => Math.min(99, q + 1))}
                        disabled={enterpriseQuantity >= 99}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3">
                    <div className="w-full rounded-xl border-2 border-sky-500 bg-sky-500/5 p-4 ring-2 ring-sky-500/25">
                      <p className="font-semibold text-foreground">Plano mensal</p>
                      <p className="mt-1 text-lg font-bold text-foreground">
                        {formatMoneyBRL(ENTERPRISE_INSTANCE_PRICE_BRL)}/mês por dispositivo
                      </p>
                    </div>
                    {/*
                    Fluxo Único (1 semana / 1 mês) — desativado no painel.
                    {enterpriseBillingType === BILLING_RECURRENT ? (
                      <div>...</div>
                    ) : (
                      <> botões WEEKLY / MONTHLY </>
                    )}
                    */}
                  </div>

                  <EnterpriseCheckoutTotalNote totalBrl={enterpriseTotalBrl(enterpriseQuantity)} />

                  <div className="mt-6 space-y-3">
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => setCreateFlowStep(CREATE_FLOW_ENTERPRISE_PAYMENT)}
                      disabled={createSubmitting}
                    >
                      Avançar
                    </Button>
                    <WavoipLegalFooter />
                  </div>
                </>
              ) : null}

              {createFlowStep === CREATE_FLOW_ENTERPRISE_PAYMENT ? (
                <>
                  <h2 className="text-center text-xl font-bold tracking-tight text-foreground">
                    Finalizar pagamento
                  </h2>
                  <p className="mt-4 text-sm font-medium text-foreground">Forma de pagamento</p>
                  <div className="mt-3 space-y-3">
                    {[
                      { id: 'PIX', label: 'Pix', Icon: BoltIcon, iconClass: 'text-teal-600' },
                      {
                        id: 'CREDIT_CARD',
                        label: 'Cartão de crédito ou débito',
                        Icon: CreditCardIcon,
                        iconClass: 'text-sky-600',
                      },
                      { id: 'BOLETO', label: 'Boleto', Icon: BanknotesIcon, iconClass: 'text-emerald-600' },
                    ].map(({ id, label, Icon, iconClass }) => {
                      const selected = enterprisePaymentMethod === id;
                      return (
                        <div
                          key={id}
                          className={cn(
                            'overflow-hidden rounded-xl border-2 transition-all',
                            selected
                              ? 'border-sky-500 bg-sky-500/5 ring-2 ring-sky-500/25'
                              : 'border-border bg-card'
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => setEnterprisePaymentMethod(id)}
                            className="flex w-full items-center gap-3 p-4 text-left"
                          >
                            <span
                              className={cn(
                                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                                selected ? 'border-sky-600 bg-sky-600' : 'border-muted-foreground/40'
                              )}
                            >
                              {selected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                            </span>
                            <span
                              className={cn(
                                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted',
                                iconClass
                              )}
                            >
                              <Icon className="h-6 w-6" />
                            </span>
                            <span className="text-sm font-medium text-foreground">{label}</span>
                          </button>
                          {selected ? (
                            <EnterpriseBillingFormFields
                              billingFullName={billingFullName}
                              setBillingFullName={setBillingFullName}
                              billingTaxId={billingTaxId}
                              setBillingTaxId={setBillingTaxId}
                              billingPhone={billingPhone}
                              setBillingPhone={setBillingPhone}
                              billingEmail={billingEmail}
                              setBillingEmail={setBillingEmail}
                              billingCountry={billingCountry}
                              setBillingCountry={setBillingCountry}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 space-y-3">
                    <Button
                      type="button"
                      className="w-full"
                      onClick={handleAdvanceFromPayment}
                      disabled={createSubmitting}
                    >
                      Avançar
                    </Button>
                    <WavoipLegalFooter />
                  </div>
                </>
              ) : null}

              {createFlowStep === CREATE_FLOW_ENTERPRISE_FINALIZE ? (
                <>
                  <h2 className="text-center text-xl font-bold tracking-tight text-foreground">
                    Finalize sua assinatura
                  </h2>

                  <div className="mt-6 divide-y divide-border rounded-xl border border-border bg-card">
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <span className="text-sm text-muted-foreground">Forma de pagamento</span>
                      <div className="flex items-center gap-2">
                        <EnterprisePaymentMethodIcon method={enterprisePaymentMethod} />
                        <span className="text-sm font-semibold text-foreground">
                          {enterprisePaymentLabel(enterprisePaymentMethod)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <span className="text-sm text-muted-foreground">Tipo de pagamento</span>
                      <div className="flex items-center gap-2">
                        <CreditCardIcon className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm font-semibold text-foreground">Recorrente</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <span className="text-sm text-muted-foreground">Frequência</span>
                      <span className="text-sm font-semibold text-foreground">
                        {enterpriseFrequencyLabel()}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <span className="text-sm text-muted-foreground">Quantidade de dispositivos</span>
                      <span className="text-sm font-semibold tabular-nums text-foreground">{enterpriseQuantity}</span>
                    </div>
                  </div>

                  <EnterpriseCheckoutTotalNote totalBrl={enterpriseTotalBrl(enterpriseQuantity)} />

                  <div className="mt-6 space-y-3">
                    <Button type="button" className="w-full" onClick={submitCreateEnterprise} disabled={createSubmitting}>
                      {createSubmitting ? (
                        <>
                          <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                          Finalizando…
                        </>
                      ) : (
                        'Finalizar'
                      )}
                    </Button>
                    <WavoipLegalFooter />
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
          <Card className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden border-border shadow-xl">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-3 top-3 z-10"
              onClick={closeDetail}
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
            <CardContent className="max-h-[92vh] overflow-y-auto p-6 pt-14 sm:p-8 sm:pt-16">
              {detailLoading && (
                <div className="flex min-h-[240px] items-center justify-center gap-2 text-muted-foreground">
                  <ArrowPathIcon className="h-6 w-6 animate-spin" />
                  Carregando informações…
                </div>
              )}
              {!detailLoading && detailError && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {detailError}
                </div>
              )}
              {!detailLoading && detailDevice && (() => {
                const d = detailDevice;
                const statusNorm = String(d.status_norm || d.status || '').toLowerCase();
                const isOpen = statusNorm === 'open';
                const title = (editName || '').trim() || d.name || 'Dispositivo';
                const initial = (title.trim().charAt(0) || '?').toUpperCase();
                const phoneLine = formatPhoneDisplay(d.phone_norm || d.phone);
                const isEnterprise = !d.is_free;
                const isRecorrente = d.id_subscription != null && d.id_subscription !== '';
                const isUnofficial =
                  String(d.call_type || '')
                    .toUpperCase()
                    .includes('UNOFFICIAL');
                const channels = typeof d.num_channels === 'number' ? d.num_channels : 1;
                const callsMade = typeof d.calls_made === 'number' ? d.calls_made : 0;
                const tokenStr = d.token ? String(d.token) : '';
                const tokenDisplay = tokenVisible ? tokenStr : maskToken(tokenStr);
                const deviceTokenNorm = tokenStr ? tokenStr.trim().toLowerCase() : '';
                const instanceInvoices = deviceTokenNorm
                  ? wavoipInvoices
                      .filter(
                        (inv) =>
                          inv.token &&
                          String(inv.token).trim().toLowerCase() === deviceTokenNorm
                      )
                      .sort(
                        (a, b) =>
                          new Date(b.due_date || 0).getTime() -
                          new Date(a.due_date || 0).getTime()
                      )
                  : [];
                const discadorBilling = d._painel?.siriusysAsaas;
                const usesDiscadorAsaas = isEnterprise && d._painel?.hideWavoipInvoices;
                const discadorPaymentPaid =
                  discadorBilling?.paymentUrl && isAsaasPaymentPaid(discadorBilling.status);
                const whatsAppBlockedByPayment =
                  usesDiscadorAsaas && (!discadorBilling?.paymentUrl || !discadorPaymentPaid);
                const dueDateDisplay = formatDateShortPt(d.due_date);
                const financeiroRows = buildFinanceiroInvoiceRows({
                  usesDiscadorAsaas,
                  discadorBilling,
                  instanceInvoices,
                  isRecorrente,
                  dueDateDisplay,
                  deviceId: d.id,
                });

                return (
                  <div className="flex flex-col gap-6 sm:flex-row">
                    <InstanceDetailSideMenu
                      section={detailMenuSection}
                      onSectionChange={setDetailMenuSection}
                    />
                    <div className="min-w-0 flex-1">
                    {detailMenuSection === DETAIL_SECTION_FINANCEIRO ? (
                      <InstanceFinanceiroPanel
                        rows={financeiroRows}
                        usesDiscadorAsaas={usesDiscadorAsaas}
                        discadorBilling={discadorBilling}
                        isRecorrente={isRecorrente}
                        invoicesLoading={invoicesLoading}
                        invoicesError={invoicesError}
                        onRefresh={refreshDeviceDetail}
                        refreshing={detailRefreshing || invoicesLoading}
                      />
                    ) : null}

                    {detailMenuSection === DETAIL_SECTION_SIP ? (
                      <InstanceSipPanel deviceId={d.id} tokenStr={tokenStr} />
                    ) : null}

                    {detailMenuSection === DETAIL_SECTION_GERAL ? (
                  <div className="space-y-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${
                          isEnterprise
                            ? 'bg-amber-400 text-amber-950'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {isEnterprise ? 'Enterprise' : 'Free'}
                      </span>
                      <span
                        className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white ${
                          isRecorrente ? 'bg-violet-600' : 'bg-muted-foreground/70'
                        }`}
                      >
                        {isRecorrente ? 'Recorrente' : 'Avulso'}
                      </span>
                      {isUnofficial && (
                        <span className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white">
                          Não oficial
                        </span>
                      )}
                    </div>

                    <div className="rounded-xl border border-border bg-muted/10 p-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div className="min-w-0 flex-1 space-y-2">
                          <Label htmlFor="wavoip-device-name" className="flex items-center gap-2 text-sm font-medium">
                            <PencilSquareIcon className="h-4 w-4 text-muted-foreground" />
                            Nome do dispositivo
                          </Label>
                          <Input
                            id="wavoip-device-name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Nome exibido"
                            className="text-lg font-semibold"
                            autoComplete="off"
                          />
                          <p className="text-xs text-muted-foreground">ID interno · {d.id}</p>
                        </div>
                        <Button
                          type="button"
                          className="shrink-0 gap-2 sm:mb-0.5"
                          onClick={saveDeviceName}
                          disabled={
                            savingName ||
                            !editName.trim() ||
                            editName.trim() === String(d.name || '').trim()
                          }
                        >
                          {savingName ? (
                            <>
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              Salvando…
                            </>
                          ) : (
                            'Salvar alterações'
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium text-muted-foreground">Token</span>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <code className="max-w-full truncate rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground">
                            {tokenDisplay}
                          </code>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0"
                              onClick={() => setTokenVisible((v) => !v)}
                              title={tokenVisible ? 'Ocultar token' : 'Mostrar token'}
                            >
                              {tokenVisible ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 shrink-0"
                              onClick={() => tokenStr && copyToken(tokenStr, d.id)}
                              disabled={!tokenStr}
                              title="Copiar token"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {whatsAppBlockedByPayment ? (
                      <div className="rounded-xl border border-amber-400/50 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          {DISCADOR_PAYMENT_PENDING_LEGEND}
                        </p>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="mt-3"
                          onClick={() => setDetailMenuSection(DETAIL_SECTION_FINANCEIRO)}
                        >
                          Ir para Financeiro
                        </Button>
                      </div>
                    ) : null}

                    {tokenStr && !whatsAppBlockedByPayment ? (
                      <WavoipWhatsAppLinkHub
                        deviceId={d.id}
                        token={tokenStr}
                        isOpen={isOpen}
                        statusNorm={d.status_norm}
                        statusFallback={d.status}
                        displayName={title}
                        phoneDisplay={phoneLine}
                        callsMade={callsMade}
                        isFree={!isEnterprise}
                        onRefreshDevice={() => loadDetailModalData(detailFetchId, { initial: false })}
                        deviceDetailRefreshing={detailRefreshing}
                        onDelete={() => deleteInstance(detailFetchId, { closeModal: true })}
                        deleteDisabled={
                          detailRefreshing || invoicesLoading || String(deletingId) === String(detailFetchId)
                        }
                        deleteBusy={String(deletingId) === String(detailFetchId)}
                      />
                    ) : null}

                    <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/20 p-5 sm:p-6">
                      {tokenStr ? (
                        <div className="mt-0">
                          <div className="mb-2 flex items-center justify-between text-sm">
                            <span className="font-medium text-foreground">Linhas e disponibilidade</span>
                            <span className="text-muted-foreground">{channels} canal(is)</span>
                          </div>
                          <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                              style={{ width: `${Math.min(100, channels * 50)}%` }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Uso conforme plano · ligações realizadas: {callsMade}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="flex min-w-0 flex-1 gap-4">
                              <span
                                className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                  isOpen
                                    ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                                    : 'bg-amber-500/20 text-amber-800 dark:text-amber-300'
                                }`}
                              >
                                {isOpen ? '● ' : '○ '}
                                {deviceConnectionLabelPt(d.status_norm, d.status)}
                              </span>
                            </div>
                            <div className="flex gap-2 self-end lg:self-start">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-10 w-10 border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10"
                                onClick={refreshDeviceDetail}
                                disabled={detailRefreshing || invoicesLoading}
                                title="Atualizar dados"
                              >
                                <ArrowPathIcon className={`h-5 w-5 ${detailRefreshing ? 'animate-spin' : ''}`} />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-10 w-10 border-destructive/40 text-destructive/80 hover:bg-destructive/10"
                                disabled
                                title="Indisponível neste painel"
                              >
                                <LinkSlashIcon className="h-5 w-5" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-10 w-10 border-destructive/40 text-destructive hover:bg-destructive/10"
                                onClick={() => deleteInstance(detailFetchId, { closeModal: true })}
                                disabled={
                                  detailRefreshing ||
                                  invoicesLoading ||
                                  String(deletingId) === String(detailFetchId)
                                }
                                title="Excluir instância"
                              >
                                {String(deletingId) === String(detailFetchId) ? (
                                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                ) : (
                                  <TrashIcon className="h-5 w-5" />
                                )}
                              </Button>
                            </div>
                          </div>

                          <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted text-lg font-bold text-foreground">
                              {initial}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-lg font-semibold text-foreground">{title}</p>
                              <p className="truncate font-mono text-sm text-muted-foreground">{phoneLine}</p>
                            </div>
                          </div>

                          <div className="mt-6">
                            <div className="mb-2 flex items-center justify-between text-sm">
                              <span className="font-medium text-foreground">Linhas e disponibilidade</span>
                              <span className="text-muted-foreground">{channels} canal(is)</span>
                            </div>
                            <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                                style={{ width: `${Math.min(100, channels * 50)}%` }}
                              />
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Uso conforme plao · ligações realizadas: {callsMade}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                      {!usesDiscadorAsaas ? (
                      <div className="flex min-h-[220px] flex-col rounded-2xl border border-border bg-muted/10 p-4 text-left lg:col-span-1">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground">Faturas desta instância</p>
                          {invoicesLoading ? (
                            <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                          ) : null}
                        </div>
                        {invoicesError ? (
                          <p className="text-xs text-destructive">{invoicesError}</p>
                        ) : null}

                        {!deviceTokenNorm ? (
                          <p className="text-sm text-muted-foreground">
                            Sem token de dispositivo — não é possível associar faturas a esta instância.
                          </p>
                        ) : null}
                        {deviceTokenNorm &&
                        !d._painel?.hideWavoipInvoices &&
                        !invoicesLoading &&
                        !invoicesError &&
                        instanceInvoices.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            Nenhuma fatura encontrada com o token deste dispositivo.
                          </p>
                        ) : null}
                        {deviceTokenNorm && !d._painel?.hideWavoipInvoices && instanceInvoices.length > 0 ? (
                          <ul className="max-h-[280px] space-y-3 overflow-y-auto pr-1">
                            {instanceInvoices.map((inv) => (
                              <li
                                key={inv.id}
                                className="rounded-lg border border-border bg-card p-3 text-left shadow-sm"
                              >
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <Badge variant={invoiceStatusBadgeVariant(inv.status)}>
                                      {invoiceFinancialStatusPt(inv.status)}
                                    </Badge>
                                    <p className="mt-2 text-lg font-bold text-foreground tabular-nums">
                                      {formatMoneyBRL(inv.total)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Vencimento (fatura) · {formatDatePt(inv.due_date)}
                                    </p>
                                    {String(inv.cycle || '').toUpperCase() === 'MONTHLY' &&
                                    (d.created_date || d.created_at) ? (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        Referência 1 mês após contratação do dispositivo:{' '}
                                        <span className="font-medium text-foreground">
                                          {formatDatePt(
                                            addCalendarMonths(
                                              parseToLocalDisplayDate(d.created_date || d.created_at),
                                              1
                                            )
                                          )}
                                        </span>
                                      </p>
                                    ) : null}
                                    {inv.cycle ? (
                                      <p className="text-xs text-muted-foreground">
                                        Ciclo · {billingCyclePt(inv.cycle)}
                                      </p>
                                    ) : null}
                                  </div>
                                  {inv.invoice_url ? (
                                    <a
                                      href={inv.invoice_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs font-medium text-primary hover:bg-muted"
                                    >
                                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                      Abrir fatura
                                    </a>
                                  ) : null}
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {d.due_date ? (
                          <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                            Vencimento do plano (dispositivo):{' '}
                            <span className="font-medium text-foreground">{formatDate(d.due_date)}</span>
                          </p>
                        ) : null}
                      </div>
                      ) : null}

                      <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', usesDiscadorAsaas ? 'lg:col-span-3' : 'lg:col-span-2')}>
                        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                          <div className="mb-3 inline-flex rounded-xl bg-violet-500/15 p-2.5 text-violet-600 dark:text-violet-300">
                            <ServerStackIcon className="h-6 w-6" />
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Linhas disponíveis
                          </p>
                          <p className="mt-1 text-xl font-bold text-foreground">
                            {channels} {channels === 1 ? 'linha' : 'linhas'}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                          <div className="mb-3 inline-flex rounded-xl bg-emerald-500/15 p-2.5 text-emerald-600 dark:text-emerald-400">
                            <PhoneArrowUpRightIcon className="h-6 w-6" />
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Chamadas realizadas
                          </p>
                          <p className="mt-1 text-xl font-bold text-foreground">
                            {callsMade} {callsMade === 1 ? 'ligação' : 'ligações'}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                          <div className="mb-3 inline-flex rounded-xl bg-sky-500/15 p-2.5 text-sky-600 dark:text-sky-300">
                            <PhoneArrowDownLeftIcon className="h-6 w-6" />
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Chamadas recebidas
                          </p>
                          <p className="mt-1 text-xl font-bold text-muted-foreground">—</p>
                          <p className="mt-1 text-xs text-muted-foreground">Não informado pela API</p>
                        </div>
                        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                          <div className="mb-3 inline-flex rounded-xl bg-orange-500/15 p-2.5 text-orange-600 dark:text-orange-300">
                            <PhoneXMarkIcon className="h-6 w-6" />
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Chamadas não atendidas
                          </p>
                          <p className="mt-1 text-xl font-bold text-muted-foreground">—</p>
                          <p className="mt-1 text-xs text-muted-foreground">Não informado pela API</p>
                        </div>
                      </div>
                    </div>

                    <details className="group rounded-xl border border-border bg-muted/10">
                      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:hidden [&::-webkit-details-marker]:hidden">
                        <span className="inline-flex items-center gap-2">
                          Todos os campos técnicos
                          <span className="text-xs font-normal text-muted-foreground">(clique para expandir)</span>
                        </span>
                      </summary>
                      <div className="border-t border-border px-2 pb-3">
                        <table className="w-full text-left text-sm">
                          <tbody className="divide-y divide-border">
                            {Object.keys(d)
                              .sort((a, b) => a.localeCompare(b))
                              .map((key) => (
                                <tr key={key} className="hover:bg-muted/30">
                                  <th className="whitespace-nowrap px-3 py-2 align-top font-mono text-xs font-medium text-muted-foreground">
                                    {key}
                                  </th>
                                  <td className="break-all px-3 py-2 text-xs text-foreground">
                                    {formatDetailValuePt(key, d[key])}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                    ) : null}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              Carregando instâncias…
            </div>
          ) : devices.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {!error && 'Nenhum dispositivo encontrado.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] text-left text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs font-medium uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Nome</th>
                    <th className="px-4 py-3">Telefone</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Ligações</th>
                    <th className="px-4 py-3">Vencimento</th>
                    <th className="px-4 py-3">Atualizado</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Flags</th>
                    <th className="px-4 py-3">Token</th>
                    <th className="px-4 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {devices.map((d) => (
                    <tr key={d.id} className="bg-card text-foreground hover:bg-muted/20">
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{d.id}</td>
                      <td className="max-w-[200px] truncate px-4 py-3" title={d.name || ''}>
                        {d.name || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">
                        {d.phone || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusBadgeVariant(d.status)}>
                          {deviceStatusShortPt(d.status)}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 tabular-nums">
                        {typeof d.calls_made === 'number' ? d.calls_made : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">{formatDate(d.due_date)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">
                        {formatDate(d.last_updated_date)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs">{callTypePt(d.call_type)}</td>
                      <td className="px-4 py-3 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {d.disabled ? (
                            <Badge variant="destructive">desativado</Badge>
                          ) : (
                            <Badge variant="outline">ativo</Badge>
                          )}
                          {d.is_free ? <Badge variant="secondary">free</Badge> : null}
                          {d.needs_restart ? <Badge variant="outline">restart</Badge> : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {d.token ? (
                          <div className="flex flex-wrap items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 px-2"
                              onClick={() => copyToken(d.token, d.id)}
                              title="Copiar token do dispositivo"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                              Copiar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1 px-2"
                              onClick={() => {
                                const url = wavoipWhatsAppQrImageUrl(d.token);
                                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              title="Abrir QR do WhatsApp em nova aba"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
                              QR
                            </Button>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1 whitespace-nowrap"
                            onClick={() => openDeviceDetail(d.id)}
                            disabled={detailLoading && detailFetchId === d.id}
                          >
                            <InformationCircleIcon className="h-4 w-4 shrink-0" />
                            Mais informações
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1 text-destructive hover:bg-destructive/10"
                            onClick={() => deleteInstance(d.id)}
                            disabled={String(deletingId) === String(d.id)}
                            title="Excluir instância"
                          >
                            {String(deletingId) === String(d.id) ? (
                              <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" />
                            ) : (
                              <TrashIcon className="h-4 w-4 shrink-0" />
                            )}
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
